import { DOCUMENT, isPlatformServer } from '@angular/common';
import { CommonModule } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, Inject, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { CountdownService } from '@core-mkt/services/countdown/countdown.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { ImagePreloadDirective } from '@core-mkt/services/image-preload/image-preload.directive';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { HeaderData } from '@core-mkt/shared/components/banner/banner/banner';
import { cloneDeep } from 'lodash-es';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import type { GraphicBanner, ResponsiveImageUrls } from './graphic-banner';
import { mockRoute } from './graphic-banner-mock-data';

@Component({
  selector: 'graphic-banner',
  templateUrl: './graphic-banner.component.html',
  styleUrls: ['./graphic-banner.component.scss'],
  standalone: true,
  imports: [CommonModule, ImagePreloadDirective, LazyImgDirective],
})
export class GraphicBannerComponent implements OnInit, OnDestroy {
  resolverData: PageDataResponse;
  pageData: GraphicBanner | null = null;
  headerData?: HeaderData;
  now: number;
  countdown: Observable<number>;
  offerEndTime: number = null;
  offerStartTime: number = null;
  displayRE = true;
  private readonly destroy$ = new Subject<void>();
  daysRemaining = false;
  theme: ThemeInterface;
  responsiveImages: ResponsiveImageUrls | null = null;

  constructor(
    private redactor: WysiwygRedactorService,
    private route: ActivatedRoute,
    private countdownService: CountdownService,
    private themeParser: ThemeParserService,
    private env: EnvService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    if (!this.route.snapshot) {
      this.resolverData = mockRoute.snapshot.data.pageData;
    } else {
      this.resolverData = cloneDeep(this.route.snapshot.data.pageData) as PageDataResponse;
    }
    const header = this.resolverData.craftData.header as
      | { aceGraphicPromoBanner?: GraphicBanner[]; aaGraphicPromoBanner?: GraphicBanner[] }
      | undefined;
    if (header) {
      this.pageData = header.aceGraphicPromoBanner ? header.aceGraphicPromoBanner[0] : header.aaGraphicPromoBanner?.[0];
    }
    if (this.resolverData.craftData.banners && this.resolverData.craftData.banners.graphicPromoBanner.length > 0) {
      this.pageData = this.resolverData.craftData.banners.graphicPromoBanner[0];
    }
    this.headerData = this.resolverData?.craftData?.headerData;
  }

  private injectPreloadLink(src: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('href', src);
    this.renderer.appendChild(this.document.head, link);
  }

  get content(): string {
    return this.pageData.graphicBannerContent;
  }
  get image(): boolean {
    return this.pageData.image.length > 0 && this.pageData.image[0].url ? true : false;
  }

  get imageAlignment(): string {
    return this.pageData.imageAligment; //Typo cant be fixed since coming from craft, and we dont change typehandles once data is in prod
  }

  get startTime(): string {
    return this.pageData.promotionStartDateTime;
  }

  get endTime(): string {
    return this.pageData.promotionEndsDateTime;
  }

  get displayPromo(): boolean {
    return this.pageData?.showHidePromoBanner;
  }

  get hideStickyBanner(): boolean {
    return this.headerData?.hideStickyBanner ?? false;
  }

  get isResponsiveMode(): boolean {
    return !!(this.pageData?.backgroundImage && this.pageData.backgroundImage.length > 1);
  }

  get responsiveAltText(): string {
    return this.pageData?.backgroundImage?.[0]?.altText || this.pageData?.image?.[0]?.altText || 'Promotional Banner';
  }

  private getBaseFilename(url: string): string {
    const urlParts = url.split('/');
    const filename = urlParts[urlParts.length - 1];
    const lastDotIndex = filename.lastIndexOf('.');
    const nameWithoutExt = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
    return nameWithoutExt.replace(/-(xs|sm|md|lg|xl)$/, '');
  }

  private getFileExtension(url: string): string {
    if (!url) return 'jpg';
    const filename = url.split('/').pop() || '';
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex === -1 ? 'jpg' : filename.substring(lastDotIndex + 1);
  }

  private constructResponsiveUrl(baseUrl: string, baseName: string, suffix: string, ext: string): string {
    const urlParts = baseUrl.split('/');
    urlParts[urlParts.length - 1] = `${baseName}-${suffix}.${ext}`;
    return urlParts.join('/');
  }

  private calculateResponsiveImages(): ResponsiveImageUrls | null {
    if (!this.isResponsiveMode) {
      return null;
    }

    // Check both backgroundImage and image arrays for the source URL
    const imageUrl = this.pageData?.backgroundImage?.[0]?.url || this.pageData?.image?.[0]?.url;

    if (!imageUrl) {
      return null;
    }

    const baseName = this.getBaseFilename(imageUrl);
    const ext = this.getFileExtension(imageUrl);

    return {
      xs: this.constructResponsiveUrl(imageUrl, baseName, 'xs', ext),
      sm: this.constructResponsiveUrl(imageUrl, baseName, 'sm', ext),
      md: this.constructResponsiveUrl(imageUrl, baseName, 'md', ext),
      lg: this.constructResponsiveUrl(imageUrl, baseName, 'lg', ext),
      xl: this.constructResponsiveUrl(imageUrl, baseName, 'xl', ext),
    };
  }

  ngOnInit() {
    if (this.pageData) {
      const src = isPlatformServer(this.platformId)
        ? this.redactor.extractFirstImageSrc(this.pageData.graphicBannerContent)
        : null;
      this.pageData.graphicBannerContent = this.redactor.bypassSanitizer(this.pageData.graphicBannerContent, 'lcp');
      if (src) this.injectPreloadLink(src);

      if (this.startTime && this.endTime) {
        this.offerEndTime = this.countdownService.getOfferEndTime(this.startTime, this.endTime);
        this.displayRE = this.countdownService.displayBanner(
          new Date(this.startTime).getTime(),
          new Date(this.endTime).getTime(),
        );
        this.offerStartTime = this.countdownService.getOfferStartTime(new Date(this.startTime).getTime());
      }

      const themeInput: ComponentTheme = {
        styles: this.pageData.styles,
        backgroundColor: this.pageData.backgroundColor,
        backgroundImage: this.pageData.backgroundImage?.[0],
        textColor: this.pageData.textColor,
        columnCount: 0,
        columnDistribution: '',
      };
      this.theme = this.themeParser.getThemeClasses(themeInput);

      // Calculate responsive images once
      this.responsiveImages = this.calculateResponsiveImages();

      // Broadcast sale state to enable hide-on-sale functionality
      const isSaleActive = this.displayPromo && this.displayRE;
      this.countdownService.setGraphicBannerSaleValue(isSaleActive);
    }
    this.countdown = this.countdownService.create(this.offerEndTime, this.offerStartTime);

    this.countdown.subscribe((data) => {
      if (data > 0) {
        const milli = data;
        const hours = milli / 1000 / 60 / 60;
        this.daysRemaining = hours > 24;
      }
    });
  }

  ngOnDestroy(): void {
    // Reset sale state when banner component is destroyed
    this.countdownService.setGraphicBannerSaleValue(false);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
